import type { NoteRecord } from '@/types/note';

export interface ExamGroup {
  key: string;
  examName: string;
  examType: string;
  subject: string;
  notes: NoteRecord[];
  latestAt: string;
}

export interface StudentGroup {
  key: string;
  studentName: string;
  exams: ExamGroup[];
  noteCount: number;
}

export const NO_STUDENT_LABEL = '이름 없음';
export const NO_EXAM_LABEL = '시험명 없음';
export const MY_NOTES_LABEL = '내 오답노트';

function makeExamKey(note: NoteRecord): string {
  const examName = note.examName.trim() || NO_EXAM_LABEL;
  return `${examName}||${note.examType}||${note.subject}`;
}

/** 노트 목록을 시험 단위로 묶습니다. (최근 순 정렬) */
function buildExamGroups(notes: NoteRecord[]): ExamGroup[] {
  const examMap = new Map<string, ExamGroup>();

  notes.forEach((note) => {
    const key = makeExamKey(note);
    if (!examMap.has(key)) {
      examMap.set(key, {
        key,
        examName: note.examName.trim() || NO_EXAM_LABEL,
        examType: note.examType,
        subject: note.subject,
        notes: [],
        latestAt: '',
      });
    }
    const exam = examMap.get(key)!;
    exam.notes.push(note);
    if (note.createdAt > exam.latestAt) exam.latestAt = note.createdAt;
  });

  return Array.from(examMap.values())
    .map((exam) => ({
      ...exam,
      notes: [...exam.notes].sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    }))
    .sort((a, b) => b.latestAt.localeCompare(a.latestAt));
}

function toGroup(studentName: string, notes: NoteRecord[]): StudentGroup {
  const exams = buildExamGroups(notes);
  const noteCount = exams.reduce((sum, exam) => sum + exam.notes.length, 0);
  return { key: studentName, studentName, exams, noteCount };
}

/**
 * 노트를 학생 이름 기준으로 1차, 시험 기준으로 2차 그룹화합니다.
 * 학생은 이름 가나다순, 시험은 최근 순으로 정렬합니다.
 */
export function groupNotes(notes: NoteRecord[]): StudentGroup[] {
  const studentMap = new Map<string, NoteRecord[]>();

  notes.forEach((note) => {
    const studentName = note.studentName.trim() || NO_STUDENT_LABEL;
    if (!studentMap.has(studentName)) studentMap.set(studentName, []);
    studentMap.get(studentName)!.push(note);
  });

  const groups: StudentGroup[] = [];
  studentMap.forEach((studentNotes, studentName) => groups.push(toGroup(studentName, studentNotes)));

  return groups.sort((a, b) => {
    if (a.studentName === NO_STUDENT_LABEL) return 1;
    if (b.studentName === NO_STUDENT_LABEL) return -1;
    return a.studentName.localeCompare(b.studentName, 'ko');
  });
}

/** 학생 계정용: 모든 노트를 하나의 파일로 묶습니다. */
export function groupNotesAsSingle(notes: NoteRecord[], studentName: string): StudentGroup | null {
  if (notes.length === 0) return null;
  return toGroup(studentName, notes);
}

export function collectStudentNames(notes: NoteRecord[]): string[] {
  const set = new Set<string>();
  notes.forEach((note) => {
    const name = note.studentName.trim();
    if (name) set.add(name);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'ko'));
}

export function latestAtOf(group: StudentGroup): string {
  return group.exams[0]?.latestAt ?? '';
}